<template>
  <div class="page-container">
    <div class="page-header">
      <h1 class="page-title">{{ pageTitle }}</h1>
      <button class="btn btn-primary" @click="handleCreate">
        <i class="ri-add-line"></i>
        新增平台
      </button>
    </div>

    <div class="page-toolbar">
      <div class="search-box">
        <input
          v-model="searchQuery"
          type="text"
          class="form-control"
          placeholder="搜尋平台代碼或平台名稱..."
          @input="handleSearch"
        />
        <i class="ri-search-line"></i>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchPlatforms">重試</button>
    </div>

    <div v-else class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>平台代碼</th>
            <th>平台名稱</th>
            <th>狀態</th>
            <th>客戶數</th>
            <th>客戶數上限</th>
            <th>建立時間</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="platforms.length === 0">
            <td colspan="7" class="empty-state">
              沒有找到平台
            </td>
          </tr>
          <tr v-for="platform in platforms" :key="platform.id">
            <td>{{ platform.platformCode }}</td>
            <td>{{ platform.platformName }}</td>
            <td>
              <span
                :class="platform.status === 'active' ? 'badge badge-success' : 'badge badge-danger'"
              >
                {{ platform.status === 'active' ? '啟用' : '停用' }}
              </span>
            </td>
            <td>{{ platform.clientCount || 0 }}</td>
            <td>{{ platform.maxClients || '無限制' }}</td>
            <td>{{ formatDate(platform.createdAt) }}</td>
            <td>
              <div class="action-buttons">
                <button
                  class="btn btn-sm btn-secondary"
                  @click="handleEdit(platform)"
                  title="編輯"
                >
                  <i class="ri-edit-line"></i>
                </button>
                <button
                  class="btn btn-sm btn-danger"
                  :disabled="platform.clientCount > 0"
                  @click="handleDelete(platform)"
                  :title="platform.clientCount > 0 ? '無法刪除：平台下仍有客戶' : '刪除'"
                >
                  <i class="ri-delete-bin-line"></i>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <ListPagination
      v-if="total > 0"
      :page="currentPage"
      :total-pages="totalPages"
      :total="total"
      @change="goToPage"
    />
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { platformManagementService } from '@/api/modules/platforms.js';
import { showSuccess, showError, showInfo, showDeleteConfirm } from '@/utils/swal.js';

export default {
  name: 'PlatformManagement',
  setup() {
    const router = useRouter();
    const { pageTitle } = usePageTitle('平台列表');
    const platforms = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const searchQuery = ref('');
    const currentPage = ref(1);
    const pageSize = ref(10);
    const total = ref(0);
    const searchTimeout = ref(null);

    const totalPages = computed(() => {
      return Math.ceil(total.value / pageSize.value);
    });

    const fetchPlatforms = async () => {
      loading.value = true;
      error.value = null;

      try {
        const params = {
          page: currentPage.value,
          pageSize: pageSize.value,
        };

        if (searchQuery.value) {
          params.search = searchQuery.value;
        }

        const response = await platformManagementService.getPlatforms(params);

        if (response && response.success && response.data) {
          platforms.value = response.data.items || [];
          total.value = response.data.total || 0;
        } else {
          error.value = response && response.message || '獲取平台列表失敗';
        }
      } catch (err) {
        console.error('獲取平台列表失敗:', err);
        error.value = err.message || '獲取平台列表失敗';
      } finally {
        loading.value = false;
      }
    };

    const handleSearch = () => {
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value);
      }
      searchTimeout.value = setTimeout(() => {
        currentPage.value = 1;
        fetchPlatforms();
      }, 500);
    };

    const goToPage = (page) => {
      if (page >= 1 && page <= totalPages.value) {
        currentPage.value = page;
        fetchPlatforms();
      }
    };

    const handleCreate = () => {
      router.push('/system/platforms/create');
    };

    const handleEdit = (platform) => {
      router.push(`/system/platforms/${platform.id}/edit`);
    };

    const handleDelete = async (platform) => {
      const confirmed = await showDeleteConfirm(`${platform.platformName} (${platform.platformCode})`);
      
      if (!confirmed) {
        return;
      }

      const originalLoading = loading.value;
      loading.value = true;
      error.value = null;

      try {
        const response = await platformManagementService.deletePlatform(platform.id);

        // axios 攔截器已經返回 response.data，所以 response 就是 { success: true, data: {...}, message: '...' }
        if (response && response.success) {
          await showSuccess('刪除成功', `平台「${platform.platformName}」已成功刪除`);
          
          if (platforms.value.length === 1 && currentPage.value > 1) {
            currentPage.value = currentPage.value - 1;
          }
          
          await fetchPlatforms();
        } else {
          const errorMsg = response?.message || '刪除平台失敗';
          await showError('刪除失敗', errorMsg);
        }
      } catch (err) {
        console.error('刪除平台失敗:', err);
        console.error('錯誤詳情:', {
          message: err.message,
          code: err.code,
          status: err.response?.status,
          data: err.response?.data,
          url: `/system/platforms/management/${platform.id}`
        });
        
        let errorTitle = '刪除失敗';
        let errorText = '刪除平台失敗，請稍後再試';
        
        if (err.code === 'HAS_RELATED_ACCOUNTS') {
          errorText = '無法刪除平台，因為仍有關聯的平台帳號';
        } else if (err.code === 'HAS_RELATED_CLIENTS') {
          errorText = '無法刪除平台，因為仍有關聯的客戶';
        } else if (err.code === 'NOT_FOUND') {
          errorText = '平台不存在或已被刪除';
        } else if (err.code === 'FORBIDDEN') {
          errorText = '您沒有權限刪除此平台';
        } else if (err.response?.status === 404) {
          errorText = '平台不存在或路由錯誤';
        } else if (err.response?.status === 403) {
          errorText = '您沒有權限刪除此平台';
        } else if (err.message) {
          errorText = err.message;
        }
        
        await showError(errorTitle, errorText);
      } finally {
        loading.value = originalLoading;
      }
    };

    const formatDate = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString('zh-TW');
    };

    onMounted(() => {
      fetchPlatforms();
    });

    return {
      platforms,
      loading,
      error,
      searchQuery,
      currentPage,
      totalPages,
      total,
      pageTitle,
      handleSearch,
      goToPage,
      handleCreate,
      handleEdit,
      handleDelete,
      formatDate,
      fetchPlatforms,
    };
  },
};
</script>

<style scoped>
@import '~@/assets/scss/components/_table-common.scss';
@import '~@/assets/scss/components/_list-page-common.scss';

/* 頁面特定樣式覆寫 */
.table-wrapper table {
  min-width: 1000px; /* 平台列表有 7 個欄位（新增客戶數） */
}
</style>
